
/**
 * Module dependencies.
 */

import { color, media, units } from 'src/styles/utils';
import { colors } from 'src/styles/colors';
import { ifProp, theme } from 'styled-tools';
import { useRouter } from 'next/router';
import CardTitle from 'src/components/core/typography/card-title';
import React, { ReactElement } from 'react';
import RouterLink from 'src/components/core/links/router-link';
import Svg from 'src/components/core/svg';
import arrowRightIcon from 'src/assets/svg/arrow-right.svg';
import styled, { css } from 'styled-components';

type Props = {
  disabled?: boolean,
  href?: string,
  icon: string,
  onClick?: () => void,
  title: string,
  total?: number
};
/**
 * `Card` styled component.
 */

const Card = styled.button.attrs<Props>(({ href }) => {
  const element = href && RouterLink || 'button';

  return {
    as: element,
    type: element === 'button' ? 'button' : null
  };
})`
  align-items: center;
  appearance: none;
  background-color: ${color('beige100')};
  border: 1px solid ${color('white')};
  color: ${color('textColor')};
  cursor: pointer;
  display: flex;
  outline: none;
  padding: 41px ${units(2.5)};
  text-decoration: none;
  transition: ${theme('animations.defaultTransition')};
  transition-property: background-color, color;
  width: 100%;

  ${media.min('ms')`
    padding: 41px 34px;
  `}

  &:focus,
  &:hover {
    color: ${color('brown400')};
  }

  ${ifProp('disabled', css`
    background-color: ${color('grey200')};
    color: ${color('grey600')};
    cursor: default;
    pointer-events: none;
  `)}
`;

/**
 * `Title` styled component.
 */

const Title = styled(CardTitle).attrs({ size: 'small' })`
  flex: 1;
  margin: 0 ${units(5)};
  text-align: left;
`;

/**
 * `Total` styled component.
 */

const Total = styled.sup<{ disabled: boolean }>`
  ${theme('typography.styles.p')}

  color: ${color('brown400')};
  font-style: italic;
  left: ${units(0.5)};

  ${ifProp('disabled', css`
    color: ${color('grey600')};
  `)}
`;

/**
 * `MenuLink` component.
 */

const MenuLink = (props: Props): ReactElement => {
  const { locale } = useRouter();
  const { href, icon, onClick, title, total } = props;

  return (
    <Card
      disabled={!href && !onClick}
      {...href ? { href, locale } : { onClick }}
    >
      <Svg
        icon={icon}
        size={units(3.5)}
      />

      <Title>
        {title}

        {total > 0 && (
          <Total disabled={!href}>
            {total}
          </Total>
        )}
      </Title>

      <Svg
        color={colors.brown200}
        icon={arrowRightIcon}
        size={units(3.5)}
      />
    </Card>
  );
};

/**
 * Export `MenuLink` component.
 */

export default MenuLink;
